import React from "react";
import * as Sentry from "@sentry/react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { withTranslation } from "react-i18next";
import { reportClientError } from "@/lib/errorReporter";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(_error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    reportClientError({
      kind: "frontend_react_crash",
      severity: "error",
      message: error?.message || "React render crash",
      stack: (error?.stack || "") + "\n\nComponent stack:\n" + (errorInfo?.componentStack || ""),
    });
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo?.componentStack } },
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { t } = this.props;
      return (
        <div className="fixed inset-0 bg-[#0a0f1a] flex items-center justify-center p-4 overflow-auto">
          <div className="bg-[#1e293b] border border-slate-700 rounded-2xl p-6 sm:p-8 max-w-sm w-full text-center">
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-amber-400" />
            </div>

            <h1 className="text-2xl font-bold text-white mb-2">{t("errorBoundary.title")}</h1>

            <p className="text-slate-400 mb-6">{t("errorBoundary.message")}</p>

            <button
              onClick={this.handleReload}
              className="flex items-center justify-center gap-2 w-full btn-tier1 font-semibold py-3 px-6 rounded-xl transition-all duration-200"
            >
              <RefreshCw className="w-4 h-4" />
              {t("errorBoundary.refresh")}
            </button>

            <p className="text-xs text-slate-400 mt-6">{t("errorBoundary.supportHint")}</p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default withTranslation()(ErrorBoundary);
